"use client"

import { useState } from 'react'
import { Plus, Minus } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

export interface FAQItem {
  question: string
  answer: string
}

interface FAQSectionProps {
  title?: string
  subtitle?: string
  faqs: FAQItem[]
}

export default function FAQSection({ title = "FREQUENTLY ASKED QUESTIONS", subtitle = "Common Queries", faqs }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section className="py-24 border-t border-white/10 relative">
      {/* Schema injected dynamically for Answer Engine Optimization (AEO) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          })
        }}
      />
      
      <div className="max-w-[1000px] mx-auto px-6">
        <div className="mb-16 text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="w-8 h-px bg-[#C8A96E]" />
            <span className="text-[#C8A96E] text-xs tracking-[0.3em]" style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}>
              {subtitle.toUpperCase()}
            </span>
            <div className="w-8 h-px bg-[#C8A96E]" />
          </div>
          <h2 className="text-4xl md:text-5xl text-[#F8FAFC]" style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.05em' }}>
            {title}
          </h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-white/10 bg-[#1E293B] overflow-hidden group hover:border-[#C8A96E]/30 transition-colors duration-300">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 md:p-8 text-left focus:outline-none"
                aria-expanded={openIndex === index}
              >
                <h3 className="text-[#F8FAFC] text-lg md:text-xl font-medium pr-8" style={{ fontFamily: 'var(--font-dm-sans, "DM Sans", sans-serif)' }}>
                  {faq.question}
                </h3>
                <div className="flex-shrink-0 text-[#C8A96E]">
                  {openIndex === index ? <Minus size={20} /> : <Plus size={20} />}
                </div>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <div className="px-6 md:px-8 pb-8 text-[#94A3B8] leading-relaxed">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
